const SEGMENTATION_DRAFT_STORAGE_KEY = "izanagi-segmentation-draft";
const SEGMENTATION_DRAFT_VERSION = 1;
const SEGMENTATION_DRAFT_MAX_BYTES = 256 * 1024;

/** 編集中の工程分解点・ラベル。未確定の request_segmentation (toolUseId) 単位で保持する。 */
export interface SegmentationDraft {
	boundaries: number[];
	labels: string[];
}

interface SegmentationDraftRecords {
	version: typeof SEGMENTATION_DRAFT_VERSION;
	records: Record<string, SegmentationDraft>;
}

function resolveSessionStorage(): Storage | null {
	try {
		return typeof window === "undefined" ? null : window.sessionStorage;
	} catch (err) {
		// sessionStorage が使えない環境でも、セグメンテーション編集自体は通常どおり続けられるようにする
		// (エラー境界が無いためここで rethrow すると編集画面全体がクラッシュする)。
		console.error("sessionStorage にアクセスできません", err);
		return null;
	}
}

function write(storage: Storage, key: string, value: string): void {
	try {
		storage.setItem(key, value);
	} catch (err) {
		console.error("セグメンテーションの下書き保存に失敗しました", err);
	}
}

function remove(storage: Storage, key: string): void {
	try {
		storage.removeItem(key);
	} catch (err) {
		console.error("セグメンテーションの下書き削除に失敗しました", err);
	}
}

function isToolUseId(value: unknown): value is string {
	return typeof value === "string" && value.trim().length > 0 && value.length <= 512;
}

function isSegmentationDraft(value: unknown): value is SegmentationDraft {
	if (typeof value !== "object" || value === null) return false;
	const draft = value as Partial<SegmentationDraft>;
	return (
		Array.isArray(draft.boundaries) &&
		draft.boundaries.every((b) => typeof b === "number" && Number.isFinite(b)) &&
		Array.isArray(draft.labels) &&
		draft.labels.every((l) => typeof l === "string")
	);
}

/**
 * 保存値を検証して安全な records だけを返す。version 不一致や records 自体の形が不正な場合は
 * 全体を破棄するが、個々の record だけが不正な場合はそれだけを除外し、他 toolUseId の記録は残す
 * (1件の破損が無関係な他リクエストの下書きまで巻き込んで消さないようにするため)。
 */
function sanitizeDraftRecords(value: unknown): SegmentationDraftRecords | null {
	if (typeof value !== "object" || value === null) return null;
	const { version, records } = value as Partial<SegmentationDraftRecords>;
	if (version !== SEGMENTATION_DRAFT_VERSION) return null;
	if (typeof records !== "object" || records === null || Array.isArray(records)) return null;
	const sanitized: Record<string, SegmentationDraft> = {};
	for (const [toolUseId, draft] of Object.entries(records)) {
		if (isToolUseId(toolUseId) && isSegmentationDraft(draft)) sanitized[toolUseId] = draft;
	}
	return { version: SEGMENTATION_DRAFT_VERSION, records: sanitized };
}

function exceedsMaxBytes(raw: string): boolean {
	return new TextEncoder().encode(raw).byteLength > SEGMENTATION_DRAFT_MAX_BYTES;
}

/** 上限に収まる JSON を返す。収まらなければ null。 */
function serializeRecords(records: Record<string, SegmentationDraft>): string | null {
	const raw = JSON.stringify({ version: SEGMENTATION_DRAFT_VERSION, records });
	return exceedsMaxBytes(raw) ? null : raw;
}

/** 保存済みの記録を読む。旧 version・records 自体が不正・上限超過はまとめて削除して無視する。 */
function readDraftRecords(storage: Storage): SegmentationDraftRecords | null {
	let raw: string | null;
	try {
		raw = storage.getItem(SEGMENTATION_DRAFT_STORAGE_KEY);
	} catch (err) {
		console.error("セグメンテーションの下書き読み取りに失敗しました", err);
		return null;
	}
	if (raw === null) return null;
	if (exceedsMaxBytes(raw)) {
		remove(storage, SEGMENTATION_DRAFT_STORAGE_KEY);
		return null;
	}

	try {
		const value: unknown = JSON.parse(raw);
		const sanitized = sanitizeDraftRecords(value);
		if (sanitized === null) {
			remove(storage, SEGMENTATION_DRAFT_STORAGE_KEY);
			return null;
		}
		return sanitized;
	} catch (err) {
		console.error("セグメンテーションの下書きが破損していたため破棄しました", err);
		remove(storage, SEGMENTATION_DRAFT_STORAGE_KEY);
		return null;
	}
}

/** 指定 request (toolUseId) の編集中データを読み取る。同じタブ内の再読み込みでのみ復元する。 */
export function readSegmentationDraft(
	toolUseId: string,
	storage: Storage | null = resolveSessionStorage(),
): SegmentationDraft | null {
	if (!isToolUseId(toolUseId) || !storage) return null;
	return readDraftRecords(storage)?.records[toolUseId] ?? null;
}

/**
 * 指定 request の編集中データを保存する。上限を超える場合、他 request の記録を古い順に
 * 必要な分だけ退避してから今回の記録を収める (無関係な他 request の下書きを不必要に失わないため)。
 */
export function writeSegmentationDraft(
	toolUseId: string,
	draft: SegmentationDraft,
	storage: Storage | null = resolveSessionStorage(),
): void {
	if (!isToolUseId(toolUseId) || !isSegmentationDraft(draft) || !storage) return;
	const current = readDraftRecords(storage);
	const records: Record<string, SegmentationDraft> = { ...current?.records, [toolUseId]: draft };
	// records の挿入順は書き込まれた順 (toolUseId の再書き込みでは順序が変わらない) なので、
	// 先頭から順に古い他 request として退避していけば古いものから捨てられる。
	const evictionOrder = Object.keys(records).filter((id) => id !== toolUseId);

	let raw = serializeRecords(records);
	while (raw === null && evictionOrder.length > 0) {
		const oldest = evictionOrder.shift();
		if (oldest !== undefined) delete records[oldest];
		raw = serializeRecords(records);
	}
	// 今回の記録単体でも上限を超える場合は保存を諦める (下書き復元が効かないだけで、
	// 編集中の内容自体は state/store 側に残るため実データは失われない)。
	if (raw === null) return;
	write(storage, SEGMENTATION_DRAFT_STORAGE_KEY, raw);
}

/** 指定 request の編集中データだけを削除する。request が決定・キャンセルされたときに呼ぶ。 */
export function clearSegmentationDraft(
	toolUseId: string,
	storage: Storage | null = resolveSessionStorage(),
): void {
	if (!isToolUseId(toolUseId) || !storage) return;
	const current = readDraftRecords(storage);
	if (!current || !(toolUseId in current.records)) return;
	const records = { ...current.records };
	delete records[toolUseId];
	if (Object.keys(records).length === 0) {
		remove(storage, SEGMENTATION_DRAFT_STORAGE_KEY);
		return;
	}
	const raw = serializeRecords(records);
	if (raw === null) return;
	write(storage, SEGMENTATION_DRAFT_STORAGE_KEY, raw);
}
